import { FixaInputField } from "@coworker/apprestructured/src/shared/wrappers/FixaInputField/FixaInputField";
import { FixaListBox } from "@coworker/apprestructured/src/shared/wrappers/FixaListBox/FixaListBox";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import FixaSearch from "@coworker/apprestructured/src/shared/wrappers/FixaSearch/FixaSearch";
import FixaChevronDownIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/ChevronDownIconPath";
import FixaChevronUpIconPath from "@coworker/apprestructured/src/shared/wrappers/FixaSSRIcon/paths/ChevronUpIconPath";
import { useTranslation } from "@coworker/locales";
import React, { useMemo, useState } from "react";
import {
  includesCaseInsensitive,
  removeAccents,
} from "../../../../helpers/utils";
import { useMerchandisingPaList } from "../../hooks/useMerchandisingPaList";
import { Media, MerchandisingPa } from "../../types/media";
import styles from "./MerchandisingPaSelect.module.css";

const sanitize = (str: string) => removeAccents(str).replace(/[.,\s]/g, "");

interface MerchandisingPaSelectProps {
  media: Media;
  onSave: (newMedia: Media) => void;
}

export const MerchandisingPaSelect = ({
  media,
  onSave,
}: MerchandisingPaSelectProps) => {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const { data: paList, isLoading, isError } = useMerchandisingPaList();
  const selectedValue = (media as MerchandisingPa).merchandisingPa ?? "";

  const options = useMemo(
    () =>
      (paList ?? []).map(({ product_area_no, product_area_name }) => {
        const name = `${product_area_no} ${product_area_name}`;

        return {
          name,
          value: product_area_no,
          searchable: sanitize(name),
        };
      }),
    [paList]
  );

  const filteredOptions = useMemo(() => {
    const sanitizedQuery = sanitize(searchInput);

    if (!sanitizedQuery) {
      return options;
    }

    return options.filter(({ searchable, value }) => {
      return (
        includesCaseInsensitive(searchable, sanitizedQuery) ||
        includesCaseInsensitive(value, sanitizedQuery)
      );
    });
  }, [options, searchInput]);

  const selectedOption = options.find(
    (option) => option.value === selectedValue
  );

  const closeDropdown = () => {
    setIsOpen(false);
    setSearchInput("");
  };

  const onSelect = (newValue: string) => {
    const newMedia = { ...media };
    (newMedia as MerchandisingPa).merchandisingPa = newValue;
    onSave(newMedia);
    closeDropdown();
  };

  const inputValue = selectedOption?.name ?? t("noneString");

  const disabled = isLoading || isError;

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <div className={styles["container"]}>
      <FixaInputField
        id={"merchandising_pa"}
        type={"text"}
        label={t("merchandisingPaString")}
        value={inputValue}
        disabled={disabled}
        data-testid={"merchandising-pa-dropdown-trigger"}
        className={styles["input"]}
        ssrIcon={isOpen ? FixaChevronUpIconPath : FixaChevronDownIconPath}
        iconPosition={"trailing"}
        onClick={handleToggle}
        iconOnClick={handleToggle}
      />

      {isOpen && (
        <div
          className={styles["backdrop"]}
          onClick={closeDropdown}
          data-testid={"merchandising-pa-dropdown-backdrop"}
        />
      )}

      <FixaListBox
        className={styles["dropdown"]}
        open={isOpen}
        position="left"
        popupMode
      >
        <div className={styles["search"]}>
          <FixaSearch
            id="merchandising-pa-search"
            value={searchInput}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setSearchInput(event.target.value);
            }}
            onClear={() => {
              setSearchInput("");
            }}
            placeholder={t("searchBasicString")}
            autoFocus
            data-testid="merchandising-pa-dropdown-search"
          />
        </div>

        <FixaListView
          id="merchandising-pa-options"
          size="small"
          showDivider={false}
        >
          {filteredOptions.map((option) => (
            <FixaListViewItem
              key={option.value}
              inset
              title={option.name}
              control="radiobutton"
              controlProps={{
                value: option.value,
                checked: selectedValue === option.value,
              }}
              onChange={() => {
                onSelect(option.value);
              }}
              showDivider={false}
              data-testid={`merchandising-pa-option-${option.value}`}
            />
          ))}

          {!filteredOptions.length && (
            <FixaListViewItem
              inset
              title={t("noResultsForString", { query: searchInput })}
              showDivider={false}
              data-testid="merchandising-pa-option-no-results"
              disabled
            />
          )}
        </FixaListView>
      </FixaListBox>
    </div>
  );
};
